import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoginPage from './LoginPage';
import MemberRegistration from './MemberRegistration';

const SocietyHomePage = ({ tenantName }) => {
    const [society, setSociety] = useState(null);
    const [showLogin, setShowLogin] = useState(false);
    const [showRegistration, setShowRegistration] = useState(false);
    const [message, setMessage] = useState('');
    const { api, auth } = useAuth();
    const navigate = useNavigate();

    // Debug: Log auth state
    console.log('SocietyHomePage: auth state:', auth);
    console.log('SocietyHomePage: auth.user:', auth?.user);

    useEffect(() => {
        if (tenantName) {
            // Fetch specific society details using simplified API
            api.get(`/api/societies/${tenantName}/`)
                .then(res => {
                    console.log('Society details:', res.data);
                    setSociety(res.data);
                })
                .catch(err => {
                    console.error("Failed to fetch society details", err);
                    // Fallback: show basic info if API fails
                    setSociety({
                        name: tenantName.toUpperCase() + " Society",
                        address: "",
                        city: "",
                        state: "",
                        pincode: ""
                    });
                });
        } else {
            // No tenant name, show main site
            setSociety({
                name: "Apartix Platform",
                address: "",
                city: "",
                state: "",
                pincode: ""
            });
        }
    }, [api, tenantName]);

    // Redirect to dashboard if already authenticated
    useEffect(() => {
        console.log('SocietyHomePage: Checking auth for redirect:', auth);
        if (auth) {
            console.log('SocietyHomePage: User is authenticated, redirecting to dashboard');
            navigate('/');
        }
    }, [auth, navigate]);

    const handleRegistrationSuccess = (successMessage) => {
        setMessage(successMessage);
        setShowRegistration(false);
        // Auto-show login after successful registration
        setTimeout(() => {
            setShowLogin(true);
            setMessage('');
        }, 2000);
    };

    const handleLoginSuccess = () => {
        console.log('SocietyHomePage: Login success, redirecting to dashboard');
        setShowLogin(false);
        setMessage('');
        // Redirect to dashboard after successful login
        navigate('/');
    };

    if (!society) return <div className="text-center p-10">Loading Society Information...</div>;

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="bg-white shadow">
                <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
                    <h1 className="text-3xl font-bold text-gray-900">{society.name}</h1>
                    <div className="space-x-2">
                        <button 
                            onClick={() => setShowLogin(true)} 
                            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                            Member Login
                        </button>
                        <button 
                            onClick={() => setShowRegistration(true)}
                            className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                            Register as Member
                        </button>
                    </div>
                </div>
            </div>
            
            <main>
                <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    <div className="bg-white shadow rounded-lg p-6">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Welcome to {society.name}</h2>
                        <p className="text-gray-600 mb-6">
                            Access your society's pages, forms, and community resources. Please log in or register to continue.
                        </p>
                        
                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                            <div className="bg-blue-50 p-4 rounded-md">
                                <h3 className="font-medium text-blue-900 mb-2">For Members</h3>
                                <p className="text-blue-700 text-sm mb-3">
                                    Log in to access forms, submit requests, and stay updated with society announcements.
                                </p>
                                <button 
                                    onClick={() => setShowLogin(true)}
                                    className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                                >
                                    Login to your account →
                                </button>
                            </div>
                            
                            <div className="bg-green-50 p-4 rounded-md">
                                <h3 className="font-medium text-green-900 mb-2">New Member?</h3>
                                <p className="text-green-700 text-sm mb-3">
                                    Register to join the society and get access to all member features.
                                </p>
                                <button 
                                    onClick={() => setShowRegistration(true)}
                                    className="text-green-600 hover:text-green-500 text-sm font-medium"
                                >
                                    Register as member →
                                </button>
                            </div>
                        </div>
                        
                        {society.address && (
                            <div className="bg-gray-50 p-4 rounded-md">
                                <h3 className="font-medium text-gray-900 mb-2">Society Address:</h3>
                                <p className="text-gray-600">
                                    {society.address}
                                    {society.city && `, ${society.city}`}
                                    {society.state && `, ${society.state}`}
                                    {society.pincode && ` - ${society.pincode}`}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Success/Error Message */}
            {message && (
                <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50">
                    {message}
                </div>
            )}

            {/* Login Modal */}
            {showLogin && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="relative bg-white p-8 rounded-lg shadow-xl max-w-md w-full mx-4">
                        <button 
                            onClick={() => setShowLogin(false)} 
                            className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl font-bold"
                        >
                            &times;
                        </button>
                        <LoginPage onSuccess={handleLoginSuccess} />
                        <div className="mt-4 text-center">
                            <p className="text-sm text-gray-600">
                                Don't have an account?{' '}
                                <button 
                                    onClick={() => {
                                        setShowLogin(false);
                                        setShowRegistration(true);
                                    }}
                                    className="text-indigo-600 hover:text-indigo-500"
                                >
                                    Register here
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Registration Modal */}
            {showRegistration && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="relative bg-white p-8 rounded-lg shadow-xl max-w-md w-full mx-4">
                        <button 
                            onClick={() => setShowRegistration(false)} 
                            className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl font-bold"
                        >
                            &times;
                        </button>
                        <MemberRegistration 
                            onSuccess={handleRegistrationSuccess}
                            onCancel={() => setShowRegistration(false)}
                        />
                        <div className="mt-4 text-center">
                            <p className="text-sm text-gray-600">
                                Already have an account?{' '}
                                <button 
                                    onClick={() => {
                                        setShowRegistration(false);
                                        setShowLogin(true);
                                    }}
                                    className="text-indigo-600 hover:text-indigo-500"
                                >
                                    Login here
                                </button>
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SocietyHomePage;